import { format } from 'date-fns';
import { Plus, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Badge } from '../components/Badge';
import { type Column, DataTable } from '../components/DataTable';
import { CreateOrgModal } from '../components/forms/CreateOrgModal';
import { type Organization, useOrganizations } from '../hooks/useAdminData';
import { useDebounce } from '../hooks/useDebounce';
import { navigate } from '../lib/navigation';
import { subscriptionStatusBadgeVariant, subscriptionStatusLabel } from '../lib/subscriptionStatus';

function planBadgeVariant(plan: string): 'default' | 'info' | 'success' | 'primary' {
  switch (plan.toLowerCase()) {
    case 'starter':
      return 'info';
    case 'growth':
      return 'success';
    case 'enterprise':
      return 'primary';
    default:
      return 'default';
  }
}

const PAGE_SIZE = 20;

const PLAN_OPTIONS = [
  { value: 'free', label: 'Free' },
  { value: 'starter', label: 'Starter' },
  { value: 'growth', label: 'Growth' },
  { value: 'professional', label: 'Professional' },
  { value: 'business', label: 'Business' },
  { value: 'enterprise', label: 'Enterprise' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'trialing', label: 'Trial' },
  { value: 'past_due', label: 'Past due' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'paused', label: 'Paused' },
];

export function Organizations() {
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('active');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<string | undefined>();
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [createOpen, setCreateOpen] = useState(false);

  const debouncedSearch = useDebounce(search, 300);

  const params = useMemo(() => {
    const p: Record<string, unknown> = {
      page,
      limit: PAGE_SIZE,
    };
    if (debouncedSearch) p.search = debouncedSearch;
    if (planFilter) p.plan = planFilter;
    if (statusFilter) p.status = statusFilter;
    if (activeFilter) p.isActive = activeFilter === 'active';
    if (sortBy) {
      p.sortBy = sortBy;
      p.sortOrder = sortOrder;
    }
    return p;
  }, [page, debouncedSearch, planFilter, statusFilter, activeFilter, sortBy, sortOrder]);

  const { data, isLoading } = useOrganizations(params);

  const columns: Column<Organization>[] = [
    {
      key: 'name',
      header: 'Organization',
      sortable: true,
      render: (row) => <span className="text-text font-medium">{row.name}</span>,
    },
    {
      key: 'plan',
      header: 'Plan',
      render: (row) => <Badge variant={planBadgeVariant(row.plan)}>{row.plan}</Badge>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <Badge variant={subscriptionStatusBadgeVariant(row.status)}>
          {subscriptionStatusLabel(row.status, row.planExpiredAt)}
        </Badge>
      ),
    },
    {
      key: 'memberCount',
      header: 'Members',
      sortable: true,
      render: (row) => <span className="text-text-secondary">{row.memberCount}</span>,
    },
    {
      key: 'leadCount',
      header: 'Leads',
      sortable: true,
      render: (row) => (
        <span className="text-text-secondary">{row.leadCount.toLocaleString()}</span>
      ),
    },
    {
      key: 'totalAiCost',
      header: 'AI Cost',
      render: (row) => (
        <span className="text-text-secondary">${parseFloat(row.totalAiCost).toFixed(2)}</span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      render: (row) => (
        <span className="text-text-secondary">
          {format(new Date(row.createdAt), 'MMM d, yyyy')}
        </span>
      ),
    },
  ];

  function handleSort(key: string, direction: 'asc' | 'desc') {
    setSortBy(key);
    setSortOrder(direction);
    setPage(1);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Organizations</h1>
        <button
          onClick={() => setCreateOpen(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-background hover:bg-primary-hover transition-colors"
        >
          <Plus size={16} />
          Create Organization
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            placeholder="Search organizations..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full pl-9 pr-4 py-2 bg-surface border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:border-primary transition-colors"
          />
        </div>

        <select
          aria-label="Filter by plan"
          value={planFilter}
          onChange={(e) => {
            setPlanFilter(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-secondary focus:outline-none focus:border-primary transition-colors"
        >
          <option value="">All Plans</option>
          {PLAN_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <select
          aria-label="Filter by status"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-secondary focus:outline-none focus:border-primary transition-colors"
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <select
          aria-label="Filter by active state"
          value={activeFilter}
          onChange={(e) => {
            setActiveFilter(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-secondary focus:outline-none focus:border-primary transition-colors"
        >
          <option value="">All</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      <div className="bg-surface rounded-xl border border-border">
        <DataTable
          columns={columns}
          data={(data?.data ?? []) as (Organization & Record<string, unknown>)[]}
          total={data?.total ?? 0}
          page={page}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          onSort={handleSort}
          onRowClick={(row) => navigate(`/organizations/${row.id}`)}
          loading={isLoading}
        />
      </div>

      <CreateOrgModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}
